// src/components/RecommendationPopup.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Heart, Phone, MapPin, Home, TrendingUp, Clock, Star, ArrowRight, Loader, Sparkles, Eye, Building, Ruler, Bath, Bed, Award, Shield, ChevronLeft, ChevronRight, Tag, Zap, Users, CheckCircle } from 'lucide-react';
import { db } from '../../firebase.config';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';

const RecommendationPopup = ({ isOpen, onClose, timeSpent }) => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState({});
  const [likedProperties, setLikedProperties] = useState({});
  const autoRotateRef = useRef(null);

  // Property types that don't have bedrooms/bathrooms
  const landPropertyTypes = ['residential-plot', 'commercial-plot', 'agricultural-land', 'plot'];

  // Fetch featured properties from Firestore
  useEffect(() => {
    if (isOpen) {
      fetchFeaturedProperties();
    }
    return () => {
      if (autoRotateRef.current) clearInterval(autoRotateRef.current);
    };
  }, [isOpen]);

  const fetchFeaturedProperties = async () => {
    setLoading(true);
    try {
      const propertiesRef = collection(db, "properties");
      const q = query(
        propertiesRef, 
        where("featured", "==", true),
        limit(8)
      );
      const querySnapshot = await getDocs(q);
      
      const properties = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        properties.push({
          id: doc.id,
          ...data,
          images: data.images || data.propertyImages || [],
          thumbnail: data.thumbnail || (data.images?.[0]) || (data.propertyImages?.[0]) || "",
          price: Number(data.price) || 0,
          bedrooms: Number(data.bedrooms) || 0,
          bathrooms: Number(data.bathrooms) || 0,
          area: Number(data.area) || 0,
          marla: Number(data.marla) || 0,
          kanal: Number(data.kanal) || 0,
          location: data.location || data.city || "",
          city: data.city || data.location || "",
          society: data.society || data.area || "",
          propertyType: data.propertyType || "residential",
          type: data.type || "sale",
          rating: data.rating || 4.5,
          reviewCount: data.reviewCount || Math.floor(Math.random() * 50) + 10,
          agentName: data.agentName || data.contactPerson || "MUhammad Islam",
          agentPhone: "+92 348 7172565",
          agentAvatar: data.agentAvatar || null,
        });
      });

      setFeaturedProperties(properties);
      // Initialize image loaded state
      const initialLoadedState = {};
      properties.forEach((_, idx) => {
        initialLoadedState[idx] = false;
      });
      setImageLoaded(initialLoadedState);
    } catch (error) {
      console.error("Error fetching featured properties:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-rotate properties every 6 seconds
  useEffect(() => {
    if (autoRotateRef.current) clearInterval(autoRotateRef.current);
    
    if (!hovered && featuredProperties.length > 1 && isOpen && !loading) {
      autoRotateRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % featuredProperties.length);
      }, 6000);
    }
    
    return () => {
      if (autoRotateRef.current) clearInterval(autoRotateRef.current);
    };
  }, [hovered, featuredProperties.length, isOpen, loading]);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Guard clause for when there are no properties
  if (!loading && featuredProperties.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-fadeIn">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles size={28} className="text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Featured Properties</h3>
          <p className="text-gray-500 text-sm mb-6">Check back soon for exclusive recommendations!</p>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all duration-300"
          >
            Continue Browsing
          </button>
        </div>
      </div>
    );
  }

  const currentProperty = featuredProperties[currentIndex];
  
  // Don't render if currentProperty is undefined (shouldn't happen but safety check)
  if (!currentProperty && !loading) {
    return null;
  }

  const isLandProperty = landPropertyTypes.includes(currentProperty?.propertyType);
  const currentImageLoaded = imageLoaded[currentIndex] || false;

  const formatPrice = (price) => {
    if (!price && price !== 0) return 'PKR 0';
    if (price >= 10000000) {
      return `PKR ${(price / 10000000).toFixed(1)}Cr`;
    }
    if (price >= 100000) {
      return `PKR ${(price / 100000).toFixed(1)}Lac`;
    }
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getPropertyTypeLabel = (type) => {
    const types = {
      'lower-portion': 'Lower Portion',
      'upper-portion': 'Upper Portion',
      'full-house': 'Full House',
      'farmhouse': 'Farmhouse',
      'banglow': 'Banglow',
      'apartment': 'Apartment',
      'residential-plot': 'Residential Plot',
      'commercial-plot': 'Commercial Plot',
      'agricultural-land': 'Agricultural Land',
      'shop': 'Shop',
      'office': 'Office',
      'godown': 'Godown/Warehouse',
      'showroom': 'Showroom',
      'restaurant': 'Restaurant/Cafe',
      'hotel': 'Hotel/Guest House',
      'plaza': 'Commercial Plaza',
    };
    return types[type] || type || 'Property';
  };

  const getAreaDisplay = () => {
    // Add null check for currentProperty
    if (!currentProperty) return null;
    
    if (currentProperty.marla > 0) {
      return `${currentProperty.marla} Marla`;
    }
    if (currentProperty.kanal > 0) {
      return `${currentProperty.kanal} Kanal`;
    }
    if (currentProperty.area > 0) {
      return `${currentProperty.area.toLocaleString()} sq.ft`;
    }
    return null;
  };

  const handleLike = (e, propertyId) => {
    e.preventDefault();
    e.stopPropagation();
    setLikedProperties(prev => ({
      ...prev,
      [propertyId]: !prev[propertyId]
    }));
  };

  const goToPrevious = () => {
    if (featuredProperties.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + featuredProperties.length) % featuredProperties.length);
  };

  const goToNext = () => {
    if (featuredProperties.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % featuredProperties.length);
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={12}
            className={`${i < fullStars ? 'text-amber-400 fill-amber-400' : 
              (i === fullStars && hasHalfStar ? 'text-amber-400 fill-amber-400' : 'text-white/30')}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-fadeIn">
      {/* Dark backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      {/* Popup Container - Full width with background image */}
      <div className="relative w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl animate-slideUp">
        
        {/* Background Image with Overlay */}
        <div className="relative h-[520px] md:h-[560px] w-full">
          {/* Loading skeleton for background */}
          {!currentImageLoaded && !loading && (
            <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-shimmer" />
          )}
          
          {/* Background Image */}
          <img
            src={currentProperty?.thumbnail || currentProperty?.images?.[0] || "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&h=800&fit=crop"}
            alt={currentProperty?.title || currentProperty?.society || "Property"}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${currentImageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(prev => ({ ...prev, [currentIndex]: true }))}
          />
          
          {/* Gradient Overlay - Dark at bottom, light at top for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20" />

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/40 backdrop-blur-md rounded-full shadow-lg transition-all duration-300 hover:scale-110 hover:bg-black/60 flex items-center justify-center group focus:outline-none border border-white/20"
            aria-label="Close popup"
          >
            <X size={18} className="text-white group-hover:text-white/90 transition-colors" />
          </button>

          {/* Timer Progress Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-white/20 z-10">
            <div 
              className="h-full bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 transition-all duration-1000 rounded-full"
              style={{ width: `${Math.min((timeSpent / 60) * 100, 100)}%` }}
            />
          </div>

          {/* Navigation Arrows */}
          {featuredProperties.length > 1 && !loading && (
            <>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  goToPrevious();
                }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 hover:bg-black/50 z-20 border border-white/20 group"
              >
                <ChevronLeft size={20} className="text-white group-hover:text-white" />
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  goToNext();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 hover:bg-black/50 z-20 border border-white/20 group"
              >
                <ChevronRight size={20} className="text-white group-hover:text-white" />
              </button>
            </>
          )}

          {/* Navigation Dots */}
          {featuredProperties.length > 1 && !loading && (
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2 z-20">
              {featuredProperties.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentIndex(idx);
                  }}
                  className={`transition-all duration-300 rounded-full ${
                    idx === currentIndex 
                      ? 'w-8 h-1.5 bg-white' 
                      : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Content Overlay - Bottom Section */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-10">
            {/* Badges Row */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 py-1 bg-emerald-500/90 backdrop-blur-sm text-white rounded-lg text-[11px] font-bold shadow-lg">
                {getPropertyTypeLabel(currentProperty?.propertyType)}
              </span>
              <span className={`px-2.5 py-1 backdrop-blur-sm rounded-lg text-[11px] font-bold flex items-center gap-1.5 shadow-lg ${
                currentProperty?.type === 'sale' 
                  ? 'bg-blue-500/90 text-white' 
                  : 'bg-purple-500/90 text-white'
              }`}>
                {currentProperty?.type === 'sale' ? <TrendingUp size={11} /> : <Home size={11} />}
                {currentProperty?.type === 'sale' ? 'FOR SALE' : 'FOR RENT'}
              </span>
              {currentProperty?.featured && (
                <span className="px-2.5 py-1 bg-amber-500/90 backdrop-blur-sm text-white rounded-lg text-[11px] font-bold flex items-center gap-1.5 shadow-lg">
                  <Star size={11} fill="white" />
                  FEATURED
                </span>
              )}
              <div className="flex items-center gap-1.5 px-2 py-1 bg-black/40 backdrop-blur-sm rounded-lg">
                {renderStars(currentProperty?.rating || 4.5)}
                <span className="text-[10px] font-medium text-white/80">({currentProperty?.reviewCount || 0})</span>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 drop-shadow-lg line-clamp-1">
              {currentProperty?.society || currentProperty?.title || 'Premium Property'}
            </h2>

            {/* Location */}
            <div className="flex items-center gap-1.5 mb-4 text-white/80">
              <MapPin size={14} className="text-emerald-400" />
              <span className="text-sm md:text-base drop-shadow">
                {currentProperty?.location}, {currentProperty?.city}
              </span>
            </div>

            {/* Features Grid */}
            <div className="flex flex-wrap gap-3 mb-4">
              {!isLandProperty ? (
                <>
                  {currentProperty?.bedrooms > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-black/40 backdrop-blur-md rounded-xl border border-white/20">
                      <Bed size={14} className="text-emerald-400" />
                      <span className="text-white text-sm font-medium">{currentProperty.bedrooms} Beds</span>
                    </div>
                  )}
                  {currentProperty?.bathrooms > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-black/40 backdrop-blur-md rounded-xl border border-white/20">
                      <Bath size={14} className="text-emerald-400" />
                      <span className="text-white text-sm font-medium">{currentProperty.bathrooms} Baths</span>
                    </div>
                  )}
                  {getAreaDisplay() && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-black/40 backdrop-blur-md rounded-xl border border-white/20">
                      <Ruler size={14} className="text-emerald-400" />
                      <span className="text-white text-sm font-medium">{getAreaDisplay()}</span>
                    </div>
                  )}
                </>
              ) : (
                getAreaDisplay() && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-black/40 backdrop-blur-md rounded-xl border border-white/20">
                    <Building size={14} className="text-emerald-400" />
                    <span className="text-white text-sm font-medium">{getAreaDisplay()}</span>
                  </div>
                )
              )}
            </div>

            {/* Price */}
            <div className="mb-5">
              <p className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
                {formatPrice(currentProperty?.price || 0)}
                {currentProperty?.type === 'rent' && <span className="text-sm font-normal text-white/70 ml-1">/month</span>}
              </p>
            </div>

            {/* Agent Info & Action Buttons Row */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Agent Section */}
              <div className="flex items-center gap-3 px-3 py-2 bg-black/40 backdrop-blur-md rounded-xl border border-white/20">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
                  <Users size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{currentProperty?.agentName || 'Property Specialist'}</p>
                  <div className="flex items-center gap-1">
                    <CheckCircle size={10} className="text-emerald-400" />
                    <p className="text-white/60 text-[10px]">Verified Agent</p>
                  </div>
                </div>
                <div className="h-8 w-px bg-white/20 mx-1" />
                <a 
                  href={`tel:${currentProperty?.agentPhone}`}
                  className="flex items-center gap-1.5 text-white/80 hover:text-white transition-colors text-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone size={14} className="text-emerald-400" />
                  <span className="hidden sm:inline">Call</span>
                </a>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => handleLike(e, currentProperty?.id)}
                  className="w-10 h-10 bg-black/40 backdrop-blur-md rounded-xl flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 border border-white/20"
                >
                  <Heart 
                    size={18} 
                    className={`transition-colors ${likedProperties[currentProperty?.id] ? 'fill-red-500 text-red-500' : 'text-white'}`}
                  />
                </button>
                
                <Link 
                  to={`/category/${currentProperty?.type}/${currentProperty?.id}`}
                  onClick={onClose}
                  className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl transition-all duration-300 flex items-center gap-2 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 group"
                >
                  <Eye size={16} />
                  View Details
                  <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>
          </div>

          {/* Loading State Overlay */}
          {loading && (
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center z-30">
              <div className="relative">
                <div className="w-16 h-16 border-2 border-emerald-500/30 rounded-full animate-ping absolute" />
                <Loader size={36} className="animate-spin text-emerald-500 relative" />
              </div>
              <p className="text-white/80 mt-5 text-sm font-medium">Finding perfect matches...</p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1);
        }
        
        .animate-shimmer {
          background: linear-gradient(90deg, #2d2d2d 0%, #3d3d3d 50%, #2d2d2d 100%);
          background-size: 200% 100%;
          animation: shimmer 1.2s infinite;
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default RecommendationPopup;